import React from 'react';
import { Link } from 'react-router-dom';
import TroubleshootingPageLayout from '../components/TroubleshootingPageLayout';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'auth-errors', label: 'Authentication errors' },
  { id: 'validation-errors', label: 'Validation & data' },
  { id: 'business-rules', label: 'Business rules' },
  { id: 'three-ds-errors', label: '3D Secure errors' },
  { id: 'quick-resolution', label: 'Quick resolution matrix' },
];

const Row = ({ code, cause, fix }) => (
  <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
    <td className="p-3 font-mono text-xs font-bold text-rose-700 whitespace-nowrap align-top">{code}</td>
    <td className="p-3 text-sm text-gray-700 align-top">{cause}</td>
    <td className="p-3 text-sm text-gray-700 align-top">{fix}</td>
  </tr>
);

const CommonErrors = () => (
  <TroubleshootingPageLayout
    currentId="common-errors"
    title="Common Errors"
    subtitle="The API error codes you'll hit most often, grouped by root cause — with the fastest fix for each."
    titleBadge="Most Frequent"
    toc={TOC}
    mainTestId="ts-common-errors-page"
    sidebarTestId="ts-common-errors-sidebar"
  >
    <section id="overview" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Overview</h2>
      <p className="text-gray-700 leading-relaxed mb-3">
        Every response from the eCOMM Acquiring API contains a 4-digit error number in the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">R1</code> field. <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">0000</code> means success; any other value means the request did not complete. The page below covers the ~15 codes that account for &gt;90% of support tickets.
      </p>
      <p className="text-sm text-gray-600">
        For the complete reference, see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] font-semibold underline">Error Codes</Link>.
      </p>
    </section>

    <section id="auth-errors" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication errors</h2>
      <p className="text-gray-700 mb-4">You'll see these before the transaction even reaches the card rails. Almost always an environment / credentials mismatch.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-left border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-100 border-b border-gray-200">
            <tr>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Code</th>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Likely cause</th>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Fast fix</th>
            </tr>
          </thead>
          <tbody>
            <Row code="0001" cause="Invalid Username or Password — typo, env mismatch (staging creds sent to live) or trailing whitespace." fix="Re-copy from your dashboard. Confirm you're posting to the right host (staging.ecomm365.com vs live)." />
            <Row code="0002" cause="Invalid APISignature — wrong action keyword or a typo such as 'refunnd'." fix="Copy the exact signature from the relevant API Reference page (e.g. `sale`, `refund`, `voidpreauth`)." />
            <Row code="0003" cause="Access for that API is denied — your profile isn't enabled for the endpoint (e.g. Recurring Billing)." fix="Email csr@ecomm365.com to request the API permission; you'll need to re-auth after it's enabled." />
            <Row code="0027" cause="Cannot process any transaction — merchant server IP is not whitelisted." fix="Submit your production egress IP(s) to eCOMM. Until whitelisted you'll get this on every call." />
          </tbody>
        </table>
      </div>
    </section>

    <section id="validation-errors" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Validation &amp; data errors</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-left border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-100 border-b border-gray-200">
            <tr>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Code</th>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Likely cause</th>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Fast fix</th>
            </tr>
          </thead>
          <tbody>
            <Row code="0014" cause="Empty / Null data field — you submitted R1, R2… with blank values." fix="Validate client-side before POSTing. Every required field listed in the relevant API Reference table must be non-empty." />
            <Row code="0015" cause="MessageID already exists — you re-used a MessageID on retry." fix="Generate a fresh UUID per outbound request. MessageID must be unique per transaction attempt." />
            <Row code="0016 / 0017" cause="Invalid / Incorrect data — malformed amount, bad email, Luhn-fail PAN, regex mismatch." fix="Check field-level format requirements on the API page. Amounts are integers in minor units (e.g. 12345 = €123.45)." />
            <Row code="0006" cause="Transaction Code already exists — you re-sent a Sale with the same TransactionCode." fix="TransactionCode must be unique across your merchant account. Best practice: prefix with env + timestamp." />
          </tbody>
        </table>
      </div>
    </section>

    <section id="business-rules" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Business-rule errors</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-left border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-100 border-b border-gray-200">
            <tr>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Code</th>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Likely cause</th>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Fast fix</th>
            </tr>
          </thead>
          <tbody>
            <Row code="0018" cause="Currency cannot be processed by Merchant Profile." fix="Confirm the currency is enabled on your merchant account or use DCC to convert." />
            <Row code="0019" cause="Card Type cannot be processed — e.g. Amex attempted on a Visa-only profile." fix="Enable the scheme on your profile or reject the card type client-side with clear messaging." />
            <Row code="0030 / 0031 / 0032" cause="Refund amount logic fails — e.g. refunding more than the sale or a full refund after a partial." fix="Track per-transaction refunded total. Once a partial refund is issued, only further partials up to the remainder are allowed." />
            <Row code="0070" cause="Cannot refund a charged-back transaction." fix="Respond to the Chargeback notification itself — don't attempt a refund on a disputed transaction." />
          </tbody>
        </table>
      </div>
    </section>

    <section id="three-ds-errors" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">3D Secure errors</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-left border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-100 border-b border-gray-200">
            <tr>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Code</th>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Likely cause</th>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Fast fix</th>
            </tr>
          </thead>
          <tbody>
            <Row code="0009" cause="Card Not Enrolled for 3DS — issuer hasn't opted in." fix="Fallback handling depends on your acquirer policy. Most merchants proceed as non-3DS where allowed." />
            <Row code="0010 / 0011" cause="Authentication Error / Not Authenticated — customer failed or abandoned the challenge." fix="Show a friendly retry screen. Do NOT call Complete Pre-Auth on a failed 3DS." />
            <Row code="0026" cause="3DS Confirm request not found — you called /3dsconfirm with the wrong parent TransactionCode." fix="Use the exact TransactionCode returned from the Sale+3DS response. Both must match." />
            <Row code="0028" cause="3DS Confirm already executed." fix="The customer double-submitted. Look up the original outcome via Transaction Lookup instead of re-confirming." />
          </tbody>
        </table>
      </div>
    </section>

    <section id="quick-resolution" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Quick resolution matrix</h2>
      <p className="text-gray-700 mb-4">Still stuck after checking the tables above? Run through this 5-step triage:</p>
      <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 pl-2">
        <li><strong>Check the endpoint.</strong> Staging goes to <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">staging.ecomm365.com</code>; production elsewhere.</li>
        <li><strong>Check the MessageID.</strong> New UUID every attempt, otherwise <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">0015</code>.</li>
        <li><strong>Check form-encoding.</strong> Your payload must be <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">application/x-www-form-urlencoded</code> and properly URL-encoded (see <Link to="/technical-documentation/appendix/url-encoding" className="text-[#1F4E79] font-semibold underline">URL Encoding</Link>).</li>
        <li><strong>Check the amount.</strong> Minor units, integer, no decimals. €123.45 → <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">12345</code>.</li>
        <li><strong>Check the IP.</strong> If every call is <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">0027</code>, your egress IP isn't whitelisted yet.</li>
      </ol>
    </section>
  </TroubleshootingPageLayout>
);

export default CommonErrors;
